import React, { useState } from "react"
import StackedBarGraph from "./StarterGraphs/StackedBarGraph"
import LineGraph from "./StarterGraphs/LineGraph"
import FullStackedBarGraph from "./StarterGraphs/FullStackedBarGraph"
import HeatMapComponent from "./StarterGraphs/HeatMap"
import Table from "./StarterGraphs/Table"
import DonutChart from "./StarterGraphs/DonutChart"
import { formatDateForKPIDashboard } from "../../utils/generalUtils"
import { FaCheckCircle, FaExclamationCircle } from "react-icons/fa"
import { FiInfo } from "react-icons/fi"

/**
 * A reusable card component that can render different types of charts and related information
 */
const BlankCard = ({
	title,
	description,
	badge,
	chartType,
	chartData,
	chartColors,
	chartHeight = 200,
	height = 300,
	series,
	xAxisKey,
	xAxisLabel,
	yAxisLabel,
	stats,
	status = "neutral", // 'good', 'warning', 'critical', 'neutral'
	legend,
	options = {},
	customComponent,
	modal = null, // Add modal component as a prop
	enableModal = false, // Boolean to enable/disable modal functionality
	customHeader = null, // Custom header component
	customFooter = null, // Custom footer component
	smallStats = false, // Add smallStats prop with default value of false
	tooltip = "" // New tooltip prop with default value of empty string
}) => {
	// Add state to control modal visibility
	const [showModal, setShowModal] = useState(false)

	// Function to open modal
	const openModal = () => {
		if (enableModal && modal) {
			setShowModal(true)
		} else if (options.onViewMore) {
			// Fallback to onViewMore if no modal but function exists
			options.onViewMore()
		}
	}

	// Function to close modal
	const closeModal = () => {
		setShowModal(false)
	}

	// Map the status value to the configuration
	const getStatusConfig = () => {
		switch (status) {
			case "great":
			default:
				return {
					bgColor: "rgba(34,197,94,0.1)", // bg-green-500/10
					borderColor: "rgba(34,197,94,0.2)", // ring-green-500/20
					textColor: "rgb(74,222,128)", // text-green-400
					icon: "up"
				}
			case "good":
				return {
					bgColor:
						"linear-gradient(90deg, rgba(99,102,241,0.1) 0%, rgba(99,102,241,0.05) 100%)",
					borderColor: "rgba(99,102,241,0.2)",
					textColor: "rgb(99,102,241)",
					icon: "up"
				}
			case "warning":
				return {
					bgColor:
						"linear-gradient(90deg, rgba(251,146,60,0.1) 0%, rgba(251,146,60,0.05) 100%)", // orange-400
					borderColor: "rgba(251,146,60,0.2)", // orange-400
					textColor: "rgb(251,146,60)", // orange-400
					icon: "up"
				}
			case "critical":
				return {
					bgColor:
						"linear-gradient(90deg, rgba(244,63,94,0.1) 0%, rgba(244,63,94,0.05) 100%)",
					borderColor: "rgba(244,63,94,0.2)",
					textColor: "rgb(244,63,94)",
					icon: "down"
				}
		}
	}

	// Status badge component
	const StatusBadge = ({ status, badge }) => {
		const config = getStatusConfig()

		return (
			<span
				style={{
					display: "inline-flex",
					alignItems: "center",
					padding: "0.25rem 0.75rem",
					borderRadius: "9999px",
					border: "1px solid",
					borderColor: config.borderColor,
					background: config.bgColor,
					boxShadow: `0 0 10px ${config.borderColor}`
				}}
			>
				{config.icon === "up" ? (
					<FaCheckCircle
						size={16}
						style={{
							marginRight: "8px",
							color: config.textColor
						}}
					/>
				) : (
					<FaExclamationCircle
						size={16}
						style={{
							marginRight: "8px",
							color: config.textColor
						}}
					/>
				)}
				<span
					style={{
						fontWeight: "500",
						fontSize: "0.875rem",
						color: config.textColor
					}}
				>
					{badge}
				</span>
			</span>
		)
	}

	// Tooltip component
	const TooltipIcon = ({ content }) => (
		<div className="text-gray-400 hover:text-gray-600 cursor-help relative group ml-2">
			<FiInfo className="w-5 h-5" />
			<div className="absolute z-50 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity duration-300 bottom-full left-1/2 transform -translate-x-1/2 -translate-y-2 px-4 py-3 bg-gray-700 text-white text-sm rounded-lg shadow-lg w-64 pointer-events-none">
				{content}
			</div>
		</div>
	)

	// Render the appropriate chart based on chartType
	const renderChart = () => {
		// Calculate adjusted chart height based on whether both legend and custom footer exist
		const adjustedChartHeight =
			legend && legend.length > 0 && customFooter ? chartHeight - 40 : chartHeight

		switch (chartType) {
			case "stacked-bar":
				return (
					<StackedBarGraph
						data={chartData}
						series1Name={series[0] || { name: "", key: "" }}
						series2Name={series[1] || { name: "", key: "" }}
						series3Name={series[2] || { name: "", key: "" }}
						series4Name={series[3] || { name: "", key: "" }}
						xAxisKey={xAxisKey}
						xAxisLabel={xAxisLabel}
						yAxisLabel={yAxisLabel}
						title=""
						colors={chartColors}
						height={adjustedChartHeight}
						{...options}
					/>
				)
			case "line":
				return (
					<LineGraph
						chartData={
							typeof chartData === "object" && chartData.series ? chartData : null
						}
						seriesData={Array.isArray(chartData) ? chartData : []}
						categories={
							typeof chartData === "object" && chartData.categories
								? chartData.categories
								: []
						}
						xAxisKey={xAxisKey}
						seriesKey={series?.[0]?.key || ""}
						xAxisLabel={xAxisLabel}
						yAxisLabel={yAxisLabel}
						title=""
						color={chartColors ? chartColors[0] : undefined}
						height={adjustedChartHeight}
						hideTitleSection={customHeader !== null}
						{...options}
					/>
				)
			case "full-stacked-bar":
				return (
					<FullStackedBarGraph
						data={chartData}
						xAxisKey={xAxisKey}
						seriesKey={series[0]?.key || ""}
						xAxisLabel={xAxisLabel}
						yAxisLabel={yAxisLabel}
						title=""
						color={chartColors[0]}
						height={adjustedChartHeight}
						{...options}
					/>
				)
			case "heatmap":
				// Transform data for heatmap (similar to what TemplateComplianceGraph does)
				const transformedData = transformDataForHeatMap(
					chartData,
					options.selectedDays || []
				)

				const customTooltip = ({ series, seriesIndex, dataPointIndex, w }) => {
					if (!series || !series[seriesIndex] || dataPointIndex === undefined) {
						return ""
					}

					// Get data from chart
					const value = series[seriesIndex][dataPointIndex]
					const dayOfWeek = w.globals.seriesNames[seriesIndex]

					// Get additional data from our original dataset
					const dataPoint = w.config.series[seriesIndex].data[dataPointIndex]

					// Show correct date for all cells
					const dateDisplay = dataPoint.actualDate

					// Check if this is out of range data
					if (dataPoint.outOfRange) {
						return `
							<div class="custom-tooltip" style="background: white; padding: 8px; border: 1px solid #E5E7EB; border-radius: 4px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
								<div style="font-weight: bold; margin-bottom: 4px;">${dayOfWeek}, ${dateDisplay}</div>
								<div>Outside selected date range</div>
							</div>
						`
					}

					// Check if this is missing data within range
					if (dataPoint.isMissingData) {
						return `
							<div class="custom-tooltip" style="background: white; padding: 8px; border: 1px solid #E5E7EB; border-radius: 4px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
								<div style="font-weight: bold; margin-bottom: 4px;">${dayOfWeek}, ${dateDisplay}</div>
								<div>No compliance data available</div>
							</div>
						`
					}

					// Normal data - show compliance
					let color
					if (value >= options.thresholds.high) {
						color = "#4ade80" // Green
					} else if (value <= options.thresholds.low) {
						color = "#f87171" // Red
					} else {
						color = "#FFCC80" // Yellow/Orange
					}

					return `
						<div class="custom-tooltip" style="background: white; padding: 8px; border: 1px solid #E5E7EB; border-radius: 4px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
							<div style="font-weight: bold; margin-bottom: 4px;">${dayOfWeek}, ${dateDisplay}</div>
							<div style="display: flex; justify-content: space-between;">
								<span>Compliance:&nbsp;</span>
								<span style="color: ${color};">${value}%</span>
							</div>
						</div>
					`
				}

				return (
					<HeatMapComponent
						data={transformedData.heatMapData}
						customTooltip={customTooltip}
						colorRanges={options.colorRanges}
						categories={transformedData.weekLabels}
						seriesNames={transformedData.orderedDays}
						height={adjustedChartHeight}
						componentHeight={height - 70} // Adjust for header and legend
						{...options}
					/>
				)
			case "table":
				return (
					<Table
						data={chartData}
						columns={options.columns || []}
						height={adjustedChartHeight}
						title={options.tableTitle || ""}
						emptyMessage={options.emptyMessage || "No data available"}
						showFooter={options.showFooter || false}
						onViewMore={enableModal && modal ? openModal : options.onViewMore}
						onRowClick={
							enableModal && modal
								? options.allowRowClickModal
									? openModal
									: options.onRowClick
								: options.onRowClick
						}
					/>
				)
			case "donut":
				if (customComponent) {
					return customComponent
				}
				// Ensure chartData is an array
				const donutData = Array.isArray(chartData)
					? chartData
					: typeof chartData === "object"
						? Object.values(chartData)
						: []

				return (
					<DonutChart
						data={donutData}
						labels={options.labels || []}
						colors={chartColors}
						height={adjustedChartHeight}
						{...options}
					/>
				)
			default:
				return <div className="p-4 text-center text-gray-500">No chart type specified</div>
		}
	}

	// Helper functions copied from TemplateComplianceGraph
	// Add the helpers for heatmap transformation
	const transformDataForHeatMap = (data, selectedDays) => {
		// Safety check
		if (!Array.isArray(data) || data.length === 0) {
			console.log("No data for heatmap transformation")
			return { heatMapData: [], weekLabels: [], orderedDays: [] }
		}

		// Helper function to get day of week (0=Sunday, 1=Monday, etc.) from YYYY-MM-DD string
		const getDayOfWeek = dateStr => {
			// Parse the date string parts
			const [year, month, day] = dateStr.split("-").map(num => parseInt(num, 10))
			// Create date at noon to avoid timezone issues
			const date = new Date(year, month - 1, day, 12, 0, 0)
			return date.getDay()
		}

		// Helper to add days to YYYY-MM-DD string
		const addDaysToDateString = (dateStr, daysToAdd) => {
			const [year, month, day] = dateStr.split("-").map(num => parseInt(num, 10))
			// Create date at noon to avoid timezone issues
			const date = new Date(year, month - 1, day, 12, 0, 0)
			date.setDate(date.getDate() + daysToAdd)

			return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
		}

		// Format date for display
		const formatDisplayDate = dateStr => {
			const [, month, day] = dateStr.split("-").map(num => parseInt(num, 10))
			const monthNames = [
				"Jan",
				"Feb",
				"Mar",
				"Apr",
				"May",
				"Jun",
				"Jul",
				"Aug",
				"Sep",
				"Oct",
				"Nov",
				"Dec"
			]
			return `${monthNames[month - 1]} ${day}`
		}

		const getFullDayName = abbr => {
			const fullNames = {
				Mon: "Monday",
				Tue: "Tuesday",
				Wed: "Wednesday",
				Thu: "Thursday",
				Fri: "Friday",
				Sat: "Saturday",
				Sun: "Sunday"
			}
			return fullNames[abbr] || abbr
		}

		// Filter and process data
		const validDataPoints = data.filter(
			item => item && item.date && item.compliance !== undefined
		)

		// Safety check again after filtering
		if (validDataPoints.length === 0) {
			console.log("No valid data points for heatmap")
			return { heatMapData: [], weekLabels: [], orderedDays: [] }
		}

		// Find date range
		validDataPoints.sort((a, b) => a.date.localeCompare(b.date))
		const startDateStr = validDataPoints[0].date
		const endDateStr = validDataPoints[validDataPoints.length - 1].date

		// Create map for quick lookups
		const dataByDate = new Map()
		validDataPoints.forEach(item => {
			dataByDate.set(item.date, item.compliance)
		})

		// Generate week dates based on the actual data range
		const calendarWeeks = []
		let currentWeekStart = startDateStr

		while (currentWeekStart <= endDateStr) {
			// Find the end of the current week (Saturday)
			const startDayOfWeek = getDayOfWeek(currentWeekStart)
			const daysToSaturday = startDayOfWeek === 6 ? 0 : 6 - startDayOfWeek
			let weekEndStr = addDaysToDateString(currentWeekStart, daysToSaturday)

			// Make sure we don't go beyond the actual end date
			if (weekEndStr > endDateStr) {
				weekEndStr = endDateStr
			}

			// Create week label
			const weekLabel = `${formatDisplayDate(currentWeekStart)} - ${formatDisplayDate(weekEndStr)}`

			// Add week to collection
			calendarWeeks.push({
				startDate: currentWeekStart,
				endDate: weekEndStr,
				label: weekLabel
			})

			// Move to next week - start from the day after the current week ends
			currentWeekStart = addDaysToDateString(weekEndStr, 1)
		}

		// Define days of week
		const allDaysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]

		// Filter by selected days if needed
		const activeDaysOfWeek =
			selectedDays.length > 0
				? allDaysOfWeek.filter(day => selectedDays.includes(getFullDayName(day)))
				: allDaysOfWeek

		// Generate heatmap data
		const heatMapData = []

		// For each week
		calendarWeeks.forEach(week => {
			// Get Sunday of this week (even if before our data starts)
			const weekStartDate = new Date(
				week.startDate.split("-")[0],
				parseInt(week.startDate.split("-")[1]) - 1,
				week.startDate.split("-")[2],
				12,
				0,
				0
			)
			const dayOfWeek = weekStartDate.getDay()
			const sundayDate = addDaysToDateString(week.startDate, -dayOfWeek)

			// For each day of the week (including those outside data range)
			activeDaysOfWeek.forEach(dayName => {
				const dayIndex = allDaysOfWeek.indexOf(dayName)

				// Calculate the actual date for this day
				const dayDate = addDaysToDateString(sundayDate, dayIndex)

				// Check if this date is within our data period
				const isInDataRange = dayDate >= startDateStr && dayDate <= endDateStr
				const hasData = dataByDate.has(dayDate)

				// Add data point for every day of the week
				heatMapData.push({
					hour: dayName,
					date: week.label,
					value: isInDataRange && hasData ? dataByDate.get(dayDate).toFixed(1) : 0,
					compliance: isInDataRange && hasData ? dataByDate.get(dayDate).toFixed(1) : 0,
					actualDate: formatDateForKPIDashboard(dayDate), // Correct formatted date
					isMissingData: !hasData, // Missing data within range
					outOfRange: !isInDataRange, // Outside data range flag
					weekStart: week.startDate,
					weekEnd: week.endDate,
					sortKey: week.startDate
				})
			})
		})

		return {
			heatMapData,
			weekLabels: calendarWeeks.map(week => week.label),
			orderedDays: activeDaysOfWeek
		}
	}

	// Render stats if provided
	const renderStats = () => {
		if (!stats) return null

		return (
			<div
				className={`grid ${options.statsGridClass || (smallStats ? "grid-cols-2" : "grid-cols-4")} text-left my-1`}
			>
				{Object.entries(stats).map(([key, value]) => (
					<div key={key} className="p-1">
						<p className="text-sm text-gray-500 capitalize">
							{key.replace(/([A-Z])/g, " $1").trim()}
						</p>
						<p className="text-lg font-semibold">{value}</p>
					</div>
				))}
			</div>
		)
	}

	return (
		<div
			className="w-full bg-white rounded-lg shadow-lg p-4"
			style={{ height: `${height}px`, display: "flex", flexDirection: "column" }}
		>
			{/* Header section - either custom header or default title/badge display */}
			{customHeader ? (
				<div className="flex items-center justify-between mb-2">
					<div className="flex-grow">{customHeader}</div>
					{badge && status !== "neutral" && <StatusBadge status={status} badge={badge} />}
				</div>
			) : (
				<div className="flex items-center justify-between mb-2">
					<div className="flex items-center">
						<h5 className="text-xl font-bold text-gray-900">{title}</h5>
						{tooltip && <TooltipIcon content={tooltip} />}
					</div>
					<div className="flex items-center space-x-2">
						{badge && status !== "neutral" && (
							<StatusBadge status={status} badge={badge} />
						)}
					</div>
				</div>
			)}

			{/* Description */}
			{description && (
				<p
					className="text-md text-left mb-2"
					dangerouslySetInnerHTML={{ __html: description }}
				/>
			)}

			{/* Stats section */}
			{renderStats()}

			{/* Chart area */}
			<div className="flex-grow" style={{ minHeight: 0 }}>
				{renderChart()}
			</div>

			{/* Footer section - show both legend and custom footer if both exist */}
			<div className="mt-2">
				{/* Legend */}
				{legend && legend.length > 0 && (
					<div className="flex flex-wrap justify-center gap-2 text-sm mb-2">
						{legend.map((item, index) => (
							<div key={index} className="flex items-center">
								<div
									className="w-2 h-2 mr-1 rounded-sm"
									style={{ backgroundColor: item.color }}
								></div>
								<span>{item.label}</span>
							</div>
						))}
					</div>
				)}

				{/* Custom Footer */}
				{customFooter && (
					<div>
						{typeof customFooter === "function"
							? customFooter({ openModal }) // Pass openModal function to the custom footer
							: customFooter}
					</div>
				)}
			</div>

			{/* Render the modal if it exists and showModal is true */}
			{enableModal && modal && React.cloneElement(modal, { showModal, closeModal })}

			{/* Custom component if provided */}
			{customComponent}
		</div>
	)
}

export default BlankCard
